/**
 * The agent portal's onboarding spine. Everything an agent has to do before
 * they can take calls is modelled here as four ordered steps, so the Setup
 * page, the progress bar, and each step's own page all agree on what is done,
 * what is next, and what is still locked.
 */

export type SetupStepId =
  | "contracting"
  | "dialer"
  | "training"
  | "desktopApp"
  | "achForm"
  | "ccAuthForm";

export interface SetupStepDefinition {
  id: SetupStepId;
  /** Short label used under the progress bar nodes. */
  label: string;
  title: string;
  description: string;
  path: string;
  /** Copy for the button that takes the agent into the step. */
  actionLabel: string;
  /** Shown when the previous step isn't finished yet. */
  lockedReason: string;
}

export const SETUP_STEPS: SetupStepDefinition[] = [
  {
    id: "contracting",
    label: "Contracting",
    title: "Create a contract",
    description:
      "Request your first carrier appointment. You have to submit one contract request before the rest of setup opens up.",
    path: "/my-agent/contracts",
    actionLabel: "Go to contracting",
    lockedReason: "",
  },
  {
    id: "dialer",
    label: "Dialer fee",
    title: "Pay the dialer fee",
    description:
      "A one-time $26 payment provisions your Ring IQ dialer, your Netrio softphone, and your Insurance Toolkit account.",
    path: "/my-agent/services/dialer",
    actionLabel: "Go to checkout",
    lockedReason: "Submit a contract request first.",
  },
  {
    id: "training",
    label: "Training videos",
    title: "Watch the training videos",
    description:
      "Nine short videos walk you through registering, funding, and running your dialer. Watch each one through and the next unlocks.",
    path: "/my-agent/setup/training",
    actionLabel: "Start training",
    lockedReason: "Pay the dialer fee first.",
  },
  {
    id: "desktopApp",
    label: "Desktop App Configuration",
    title: "Configure and calibrate desktop app",
    description:
      "Install the Netrio desktop application, sign in with your assigned credentials, and point your call delivery number at it.",
    path: "/my-agent/setup/desktop-app",
    actionLabel: "Open desktop app setup",
    lockedReason: "Finish the training videos first.",
  },
  {
    id: "achForm",
    label: "ACH form",
    title: "Sign your ACH form",
    description: "Share the bank account we'll use to send your platform refunds.",
    path: "/my-agent/documents/ach-form",
    actionLabel: "Sign the ACH form",
    lockedReason: "Configure your desktop app first.",
  },
  {
    id: "ccAuthForm",
    label: "Card auth",
    title: "Authorize your card",
    description: "Authorize the recurring weekly funding charges to the card on file.",
    path: "/my-agent/documents/cc-auth-form",
    actionLabel: "Sign the authorization",
    lockedReason: "Sign your ACH form first.",
  },
];

export interface TrainingSubStep {
  id: string;
  title: string;
  /** Wistia media id — embedded as an iframe rather than linked out. */
  mediaId: string;
  summary: string;
  notes?: string[];
  important?: string;
}

export const TRAINING_SUB_STEPS: TrainingSubStep[] = [
  {
    id: "register",
    title: "How to register",
    mediaId: "qah4l7e2a7",
    summary: "Create your Ring IQ account and get your login working.",
    important:
      "Only use your first and last name when setting up your dialer account. Do not use a business name or \"Epic Life\".",
    notes: [
      "You cannot register with a softphone, an IVR menu, a voice-automated phone system, or Google Voice. Ring only works with a direct mobile number or a direct landline.",
    ],
  },
  {
    id: "funding",
    title: "Funding your account / company",
    mediaId: "vroy6vtc2b",
    summary: "Add a funding source so your account can buy calls.",
  },
  {
    id: "call-delivery",
    title: "Set your call delivery number",
    mediaId: "rv2535vv5t",
    summary: "Point Ring at the number we assigned you so routing stays accurate.",
    notes: [
      "Epic agents must use the assigned call delivery number we provide so routing stays accurate.",
      "Do not add your own phone number in Ring. Only the assigned call delivery number is supported — the desktop app configuration step covers this in full.",
    ],
  },
  {
    id: "bids",
    title: "Create and manage bids",
    mediaId: "7d00tsync7",
    summary: "Set up the call programs that decide which calls reach you.",
    important:
      "You do not need Call Program One or Call Program Two from the video. Only set up CS FE 01x and CS FE 03x.",
    notes: ["Required call programs: CS FE 01x and CS FE 03x."],
  },
  {
    id: "calls-on-off",
    title: "Turn calls on & off",
    mediaId: "rd317jtk2a",
    summary: "Start and stop your call flow when you go on and off shift.",
  },
  {
    id: "credit",
    title: "Request credit",
    mediaId: "3dwcj22i9i",
    summary: "Submit a credit request when a call does not meet the criteria.",
  },
  {
    id: "settings",
    title: "Platform settings",
    mediaId: "xrv4uevaik",
    summary: "Tune the platform settings that control your day-to-day dialing.",
  },
  {
    id: "reporting",
    title: "Reporting",
    mediaId: "4af2oswyzr",
    summary: "Read your reports to see what your call spend is returning.",
  },
  {
    id: "recordings",
    title: "How to access call recordings",
    mediaId: "xx60e17gso",
    summary: "Pull call recordings back up for review and compliance.",
  },
];

/**
 * Fraction of a video that counts as watched — the whole thing. Wistia measures
 * this in unique seconds, so seeking past a section never counts toward it.
 */
export const TRAINING_WATCHED_THRESHOLD = 1;

/**
 * Slack in the comparison for rounding only, not a discount on the threshold
 * above: percentWatched is secondsWatched / floor(duration), so a video played
 * straight through can land a hair under 1 without a second being missed.
 */
export const TRAINING_WATCHED_EPSILON = 0.005;

export function trainingEmbedUrl(mediaId: string): string {
  // Wistia's iframe embed. We size it with our own aspect-ratio box rather than
  // videoFoam, so E-v1 (loaded for the player API) doesn't fight our layout.
  // The bracketed plugin option — URL-encoded, as Wistia's iframe embeds
  // require — turns closed captions on by default rather than leaving them to
  // the agent.
  return (
    `https://fast.wistia.net/embed/iframe/${mediaId}?playerColor=6544d2`
    + "&plugin%5Bcaptions-v1%5D%5BonByDefault%5D=true"
  );
}

export function formatCallDeliveryNumber(value: string | null | undefined): string {
  if (!value) return "Assigned after provisioning finishes";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return value;
}

export interface SetupProgress {
  /** True once the agent has submitted at least one contract request. */
  contracting: boolean;
  /** True once the dialer fee has been paid. */
  dialer: boolean;
  /** True once every training sub-step has been watched through. */
  training: boolean;
  /** True once the agent has confirmed their desktop app is calibrated. */
  desktopApp: boolean;
  /** True once the ACH form is signed. */
  achForm: boolean;
  /** True once the card authorization is signed. */
  ccAuthForm: boolean;
}

export const EMPTY_SETUP_PROGRESS: SetupProgress = {
  contracting: false,
  dialer: false,
  training: false,
  desktopApp: false,
  achForm: false,
  ccAuthForm: false,
};

/**
 * Index of the step the agent should work on next, or `SETUP_STEPS.length`
 * when everything is done. Steps are strictly ordered, so the first unfinished
 * step is the active one and everything after it is locked.
 */
export function activeSetupStepIndex(progress: SetupProgress): number {
  const index = SETUP_STEPS.findIndex((step) => !progress[step.id]);
  return index === -1 ? SETUP_STEPS.length : index;
}

export type SetupStepState = "complete" | "current" | "locked";

export function setupStepState(index: number, progress: SetupProgress): SetupStepState {
  const step = SETUP_STEPS[index];
  if (step && progress[step.id]) return "complete";
  return index === activeSetupStepIndex(progress) ? "current" : "locked";
}
